<template>
  <div>
    <main-layout
      controlsBg="light"
      :hasControlsBar="false"
      :breadcrumb="getBreadcrumb">
      <div
        class="container">
        <grid-row
          column-align="center">
          <grid-col
            col-size="4">
            <h1
              class="header margin-top-xl">
              <div>
                <chevron
                  @click="$router.push({name: 'Workspace'})"/>
                {{ $t('variableManagement.title') }}
              </div>
              <GoToTagsAndCategorical
                origin="VariableManagement"/>
            </h1>
            <alert
              v-if="alertVisible"
              :message="alertMessage"
              :type="alertType">
            </alert>
          </grid-col>
        </grid-row>
        <grid-row  class="margin-top-xl" column-align="center">
          <grid-col col-size="4">
            <variable-table
              :data="getData"
              v-on:onRetrieveVariables="retrieveVariablesPage"
              v-on:onEdit="onEdit"/>
          </grid-col>
        </grid-row>
      </div>
    </main-layout>
  </div>
</template>

<script>
import MainLayout from '../MainLayout';
import Chevron from '@/components/shared/Chevron';
import GridRow from '@/components/shared/grid/GridRow';
import GridCol from '@/components/shared/grid/GridCol';
import TableComponent from '@/components/shared/table/components/TableComponent';
import TableColumn from '@/components/shared/table/components/TableColumn';
import {mapActions, mapGetters} from 'vuex';
import FontAwesomeIcon from '@fortawesome/vue-fontawesome';
import {faPencilAlt} from '@fortawesome/pro-regular-svg-icons';
import Tags from '@/components/variable/Tags';
import BtnPill from '@/components/shared/buttons/BtnPill';
import Alert from '@/components/shared/alert/Alert';
import VariableTable from '@/components/variable/VariableTable';
import GoToTagsAndCategorical from '@/components/variable/GoToTagsAndCategorical';
import variableApi from '@/api/variable';

export default {
  name: 'VariableManagement',
  components: {
    MainLayout,
    Chevron,
    GridRow,
    GridCol,
    TableComponent,
    TableColumn,
    FontAwesomeIcon,
    Tags,
    BtnPill,
    Alert,
    VariableTable,
    GoToTagsAndCategorical
  },
  data () {
    return {
      icon: {
        edit: faPencilAlt
      },
      variableData: {},
      filter: '',
      page: 1,
      sort: {
        order: '',
        fieldName: ''
      },
      tablePageSize: 10,
      alertVisible: false,
      alertMessage: '',
      alertType: '', // error, warning, success
      paginatedVariables: {}
    };
  },
  created () {
    this.retrieveAggregateVariables({ context: this });
    this.retrieveCategories({ context: this });
    const alert = this.$route.params.alert;
    if (alert !== undefined) {
      showAlert(this, this.$t(alert), 'success');
    }
  },
  computed: {
    ...mapGetters([
      'getAggregateOperatedVariables',
      'getAggregateGradualVariables',
      'getAggregateMetricalVariables'
    ]),
    getBreadcrumb () {
      return [
        { name: this.$t('menu.workspace'), link: '/workspace' },
        { name: this.$t('variableManagement.title'), link: '/variable-management', disabled: true }
      ];
    },
    getData () {
      return this.variableData;
    }
  },
  methods: {
    ...mapActions([
      'retrieveAggregateVariables',
      'retrieveCategories'
    ]),
    onEdit (row) {
      if (row.aggregate) {
        const aggregateVariable = this.getAggregateGradualVariables.find(variable => variable.id === row.id);
        if (aggregateVariable) {
          aggregateVariable.categoryId = row.categoryId;
          this.$router.push({name: 'AggregateGradualVariable', params: aggregateVariable, query: {origin: 'VariableManagement'}});
        }
        const operatedVariable = this.getAggregateOperatedVariables.find(variable => variable.id === row.id);
        if (operatedVariable) {
          operatedVariable.categoryId = row.categoryId;
          this.$router.push({name: 'AggregateOperatedVariable', params: operatedVariable, query: {origin: 'VariableManagement'}});
        }
        const metricalVariable = this.getAggregateMetricalVariables.find(variable => variable.id === row.id);
        if (metricalVariable) {
          metricalVariable.categoryId = row.categoryId;
          this.$router.push({name: 'AggregateMetricalVariable', params: metricalVariable, query: {origin: 'VariableManagement'}});
        }
      } else {
        this.$router.push({name: 'RawVariable', params: row, query: {origin: 'VariableManagement'}});
      }
    },
    getVariableOrigin (row) {
      return row.aggregate ? this.$t('variableManagement.aggregate') : this.$t('variableManagement.raw');
    },
    getVariableType (row) {
      return row.categorized ? this.$t('variableManagement.categorical') : this.$t('variableManagement.quantitative');
    },
    categoricalTooltip (row) {
      if (!row.categorized) {
        return '';
      }
      let tooltipText = '';
      const rowCategory = this.getCategories.find(category => category.id === row.categoryId);
      if (rowCategory !== undefined) {
        rowCategory.ranges.forEach(categoryRange => {
          tooltipText += categoryRange.value + ': ' + categoryRange.name + '</br>';
        });
      }
      return tooltipText;
    },
    fetchData ({filter, sort, page}) {
      if (filter !== this.filter || sort.fieldName !== this.sort.fieldName || sort.order !== this.sort.order) {
        page = 1;
      }
      this.filter = filter;
      this.page = page === null || isNaN(page) ? 1 : page;
      this.sort.fieldName = sort.fieldName;
      this.sort.order = sort.order;
      return retrieveVariablesPaginated({context: this, page: this.page, filter: filter, order: sort});
    },
    retrieveVariablesPage ({page, filter, sort}) {
      if (this.page !== page || this.filter !== filter || sort.fieldName !== this.sort.fieldName || sort.order !== this.sort.order) {
        this.page = page || 1;
        this.filter = filter;
        this.sort.fieldName = sort.fieldName;
        this.sort.order = sort.order;
        variableApi.getPaginatedVariables(this, this.page, this.filter.toUpperCase(), this.sort.fieldName, this.sort.order, '')
          .then(data => {
            this.variableData = {
              data: data.body.variables,
              pagination: {
                totalPages: data.body.totalPages,
                currentPage: data.body.currentPage,
                count: data.body.count
              }
            };
          });
      }
    }
  }
};

const retrieveVariablesPaginated = ({context, page, filter, order}) => {
  return new Promise((resolve, reject) => {
    variableApi.getPaginatedVariables(context, page, filter.toUpperCase(), order.fieldName, order.order, [])
      .then(data => {
        resolve({
          data: data.body.variables,
          pagination: {
            totalPages: data.body.totalPages,
            currentPage: data.body.currentPage,
            count: data.body.count
          }
        }, error => {
          reject(error);
        });
      });
  });
};

const showAlert = (context, message, type) => {
  context.alertVisible = true;
  context.alertMessage = message;
  context.alertType = type;
  context.showAlert = true;
  setTimeout(() => {
    context.alertVisible = false;
  }, 2000);
};
</script>

<style lang="scss" scoped>
  @import "src/stylesheets/variables";

  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .btn-pill-wrapper {
    display: flex;
    align-items: center;
  }
</style>



// WEBPACK FOOTER //
// src/layouts/variables/VariableManagement.vue